import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Moon, Sun, ShieldCheck, ChevronDown, CalendarClock,
Megaphone, Headphones, Rocket, LayoutDashboard as WebIcon, LayoutDashboard, MonitorSmartphone, SearchCheck, UserCircle } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/lib/theme-context';
import { useSession } from '@/lib/auth/auth-client';
import AccountMenu from '@/components/AccountMenu';
import { Picture } from '@/components/media/Picture';
import { brandMark } from '@/lib/media/case-images';
import { useExclusiveMenu } from '@/lib/nav-menu-bus';
import { brand } from '@/config/brand';

const GRAD = '#5048E5';

const INDIGO = 'hsl(243 75% 59%)';
const CORAL  = 'hsl(14 100% 57%)';

const SERVICE_ITEMS = [
{ id: 'company-setup', slug: 'vision-to-launch', Icon: Rocket, label: 'Vision to Launch', accent: INDIGO },
{ id: 'ai-web-app-design', slug: 'ai-web-app-design', Icon: WebIcon, label: 'AI Web & App Design', accent: INDIGO },
{ id: 'client-portal', slug: 'client-portal', Icon: MonitorSmartphone, label: 'Branded Client Portal & App', accent: INDIGO },
{ id: 'marketing', slug: 'marketing', Icon: Megaphone, label: 'Marketing', accent: INDIGO },
{ id: 'ongoing-support', slug: 'ongoing-support', Icon: Headphones, label: 'Ongoing Support', accent: INDIGO },
{ id: 'software-audit', slug: 'software-audit', Icon: SearchCheck, label: 'Software Audit & Consolidation', accent: CORAL }];

const SERVICES_LABEL = 'Services';


export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useExclusiveMenu('site-header');
  const [servicesOpen, setServicesOpen] = useState(false);
  
  const servicesRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { theme, toggle } = useTheme();
  const { user, isAuthenticated } = useSession();
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  // Vertical swipe-up to close the mobile menu. Unlike a generic swipe hook,
  // this intentionally tracks gestures that begin on links/buttons — the whole
  // menu is links, and a real upward swipe never triggers a tap/navigation.
  const menuTouch = {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      swipeStart.current = { x: t.clientX, y: t.clientY };
    },
    onTouchEnd: (e: React.TouchEvent) => {
      if (!swipeStart.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - swipeStart.current.x;
      const dy = t.clientY - swipeStart.current.y;
      swipeStart.current = null;
      if (dy <= -50 && Math.abs(dy) > Math.abs(dx)) setOpen(false);
    },
    onTouchCancel: () => {
      swipeStart.current = null;
    },
  };

  const links = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/case-studies', label: 'Case Studies' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact\u00A0Us' }];


  const linksBeforeServices = links.slice(0, 2); // Home, About
  const linksAfterServices = links.slice(2); // Case Studies, Blog, Contact Us
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const isActive = (href: string) => location.pathname === href || (href !== '/' && location.pathname.startsWith(href + '/'));
  const isServicesActive = location.pathname.startsWith('/services');

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Collapse mobile services section when the burger menu closes.
  useEffect(() => {
    if (!open) setMobileServicesOpen(false);
  }, [open]);





  return (
    <header className="sticky top-0 z-50">
      {/* Transparent, blurred backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'var(--header-bg)',
          backdropFilter: 'blur(20px) saturate(160%)',
          WebkitBackdropFilter: 'blur(20px) saturate(160%)'
        }} />

      

      
      <div className="relative mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-3">
          <Picture
            {...brandMark}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 shrink-0 rounded-xl object-cover"
            width={36}
            height={36}
            fetchPriority="high" />
          
          <span className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--foreground))' }}>
            {brand.name}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 lg:flex">
          {linksBeforeServices.map(({ href, label }) =>
          <Link
            key={href}
            to={href}
            className="nav-link-hover text-sm font-medium transition-colors"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              color: isActive(href) ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))'
            }}>
              {label}
            </Link>
          )}

          {/* Services dropdown */}
          <div
            ref={servicesRef}
            className="relative"
            onMouseEnter={() => {
              if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
              setServicesOpen(true);
            }}
            onMouseLeave={() => {
              closeTimer.current = setTimeout(() => setServicesOpen(false), 150);
            }}>
            <Link
              to="/services"
              className="nav-link-hover flex items-center gap-1 text-sm font-medium transition-colors"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                color: isServicesActive ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
              }}>
              {SERVICES_LABEL}
              <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </Link>

            <AnimatePresence>
              {servicesOpen &&
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.97 }}
                transition={{ duration: 0.18 }}
                className="absolute left-1/2 top-full mt-3 w-64 -translate-x-1/2 rounded-2xl p-2 shadow-xl"
                style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
                  {SERVICE_ITEMS.map(({ id, slug, Icon, label, accent }) =>
                <Link
                  key={id}
                  to={`/services/${slug}`}
                  onClick={() => setServicesOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors hover:opacity-80"
                  style={{ color: 'hsl(var(--foreground))', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      <Icon size={14} style={{ color: accent, flexShrink: 0 }} />
                      {label}
                    </Link>
                )}
                </motion.div>
              }
            </AnimatePresence>
          </div>

          {linksAfterServices.map(({ href, label }) =>
          <Link
            key={href}
            to={href}
            className="nav-link-hover text-sm font-medium transition-colors"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              color: isActive(href) ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))'
            }}>
              {label}
            </Link>
          )}


        </nav>

        {/* Right side */}
        <div className="hidden items-center gap-3 lg:flex">
          {isAuthenticated ?
          <AccountMenu /> :

          <Link
            to="/login"
            className="btn-glass-hover rounded-full px-4 py-1.5 text-sm font-semibold"
            style={{
              background: 'var(--glass-bg)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--glass-shadow)',
              color: 'var(--glass-color)',
              fontFamily: 'Plus Jakarta Sans, sans-serif'
            }}>
            Login
          </Link>
          }
          <Link
            to="/get-started"
            className="btn-glow rounded-full px-4 py-1.5 text-sm font-bold"
            style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Get started
          </Link>
          <button onClick={toggle} className="icon-btn-hover ml-1" style={{ color: 'var(--on-brand)' }} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Mobile: theme toggle + hamburger — logo left, utilities right */}
        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={toggle} className="icon-btn-hover p-2" style={{ color: 'hsl(var(--foreground))' }} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="icon-btn-hover p-2"
            style={{ color: 'hsl(var(--foreground))' }}
            aria-label="Toggle menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open &&
      <div {...menuTouch} className="relative border-t pb-5 pt-4 lg:hidden" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--card))' }}>
          <nav className="flex flex-col gap-1 px-6">
            {/* Primary CTA — first and visually distinct on mobile. */}
            <Link
              to="/get-started"
              onClick={() => setOpen(false)}
              className="mb-3 flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold shadow-lg"
              style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <CalendarClock size={16} /> Get started
            </Link>
            {linksBeforeServices.map(({ href, label }) =>
          <Link
            key={href}
            to={href}
            onClick={() => setOpen(false)}
            className="rounded-xl px-3 py-3 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: 'hsl(var(--foreground))' }}>
                {label}
              </Link>
          )}
            {/* Mobile Services section — collapsed by default */}
            <div className="rounded-xl px-3 py-2">
              <button
                onClick={() => setMobileServicesOpen(v => !v)}
                className="mb-2 flex w-full items-center justify-between text-sm font-bold"
                style={{ color: GRAD }}>
                <span>{SERVICES_LABEL}</span>
                <ChevronDown size={16} className={`shrink-0 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="flex flex-col gap-1 pl-1">
                  {SERVICE_ITEMS.map(({ id, slug, Icon, label, accent }) =>
                <Link
                  key={id}
                  to={`/services/${slug}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:opacity-80"
                  style={{ color: 'hsl(var(--muted-foreground))', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    <Icon size={13} style={{ color: accent, flexShrink: 0 }} />
                    {label}
                  </Link>
              )}
                </div>
              )}
            </div>
            {linksAfterServices.map(({ href, label }) =>
          <Link
            key={href}
            to={href}
            onClick={() => setOpen(false)}
            className="rounded-xl px-3 py-3 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: 'hsl(var(--foreground))' }}>
                {label}
              </Link>
          )}
            <div className="mt-3 flex flex-col gap-2">
              {isAuthenticated ?
            <>
                  <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold" style={{ color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))' }}>
                      <UserCircle size={14} /> Profile
                    </Link>
                  {!(user as any)?.isAdmin &&
              <Link to="/dashboard" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold" style={{ color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))' }}>
                      <LayoutDashboard size={14} /> Dashboard
                    </Link>
              }
                  {(user as any)?.isAdmin &&
              <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold" style={{ color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))' }}>
                      <ShieldCheck size={14} /> Admin Panel
                    </Link>
              }
                </> :

            <Link to="/login" onClick={() => setOpen(false)} className="rounded-full border px-5 py-2.5 text-center text-sm font-semibold" style={{ borderColor: 'var(--on-brand)', color: 'var(--on-brand)' }}>Login</Link>
            }
            </div>
          </nav>
        </div>
      }
    </header>);

}